import React, { useCallback, useEffect, useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { apiCall } from './_shared';
import { GapFill, printRota } from './RotaTools';

const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit' });
const shift = (iso, n) => { const t = new Date(`${iso}T12:00:00`); t.setDate(t.getDate() + n); return t.toISOString().slice(0, 10); };

const Cell = ({ c, past, username, onClear }) => {
  if (!c.planned) return <td className="py-1 px-1 text-center text-slate-300" data-testid={`rota-cell-${username}-${c.day}`}>·</td>;
  const tone = past ? (c.scanned ? (c.late ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900') : 'bg-rose-100 text-rose-800') : (c.assigned ? 'bg-orange-50 text-orange-900 border border-orange-200' : 'bg-sky-50 text-sky-900');
  const clearable = c.assigned && !past && onClear;
  return (
    <td className="py-1 px-1 text-center" data-testid={`rota-cell-${username}-${c.day}`}>
      <span onClick={clearable ? () => onClear(username, c.day) : undefined} title={clearable ? 'Manager assignment — click to remove' : undefined} className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${tone} ${clearable ? 'cursor-pointer hover:line-through' : ''}`}>{c.planned}{c.swapped ? ' ⇄' : ''}{c.assigned ? ' *' : ''}{past ? (c.scanned ? (c.late ? ` +${c.late}′` : ' ✓') : ' ✕') : ''}</span>
    </td>
  );
};

const RotaRow = ({ r, today, onClear }) => {
  const list = r.cells || [];
  const cells = list.map((c) => <Cell key={c.day} c={c} past={c.day < today} username={r.username} onClear={onClear} />);
  return (
    <tr className="border-t" data-testid={`rota-row-${r.username}`}>
      <td className="py-1 pr-2 font-semibold text-slate-900 text-xs whitespace-nowrap">{r.username}<br /><span className="text-[10px] text-slate-500 font-normal">{(r.role || '').replace('_', ' ')}</span></td>
      {cells}
      <td className="py-1 pl-2 text-right text-[11px] text-slate-500">{r.planned_days}d</td>
    </tr>
  );
};

export const RotaGrid = ({ canManage, onClose }) => {
  const [start, setStart] = useState(() => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Podgorica' }));
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const [gap, setGap] = useState(null);
  const load = useCallback(async () => {
    try { setData(await apiCall(`/api/admin/attendance/rota?start=${start}`)); } catch (e) { setMsg(e.message); }
  }, [start]);
  useEffect(() => { load(); }, [load]);
  const clearPlan = async (username, day) => {
    if (!window.confirm(`Remove the assignment of ${username} on ${day}?`)) return;
    try { await apiCall(`/api/admin/attendance/day-plan/${username}/${day}`, { method: 'DELETE' }); setMsg(`Assignment of ${username} on ${day} removed.`); await load(); } catch (e) { setMsg(e.message); }
  };
  if (!data) return <div className="border rounded-xl p-3 bg-slate-50 mb-3 text-xs text-slate-500" data-testid="att-rota">{msg || 'Loading rota…'}</div>;
  const cov = data.coverage;
  const days = data.days || [];
  const dataRows = data.rows || [];
  const gaps = cov.gaps || [];
  const emptyDays = cov.empty_days || [];
  const perDayTotals = cov.per_day || {};
  const roleEntries = Object.entries(cov.roles || {});
  const heads = days.map((d) => <th key={d} className={`pb-1 px-1 text-center ${d === data.today ? 'text-orange-600' : ''} ${emptyDays.includes(d) ? 'text-rose-700' : ''}`}>{dayLabel(d)}</th>);
  const rows = dataRows.map((r) => <RotaRow key={r.username} r={r} today={data.today} onClear={canManage ? clearPlan : null} />);
  const totals = days.map((d) => <td key={d} className={`py-1 px-1 text-center text-[11px] font-bold ${perDayTotals[d] === 0 ? 'text-rose-700' : 'text-slate-700'}`} data-testid={`rota-total-${d}`}>{perDayTotals[d]}</td>);
  const roleRows = roleEntries.map(([role, perDay]) => {
    const roleCells = days.map((d) => {
      const isGap = perDay[d] === 0;
      const tappable = isGap && canManage && d >= data.today;
      return (
        <td key={d} onClick={tappable ? () => setGap({ role, day: d }) : undefined} className={`py-0.5 px-1 text-center text-[11px] ${isGap ? 'bg-rose-50 text-rose-700 font-bold' : 'text-slate-600'} ${tappable ? 'cursor-pointer underline decoration-dotted hover:bg-rose-100' : ''}`} title={tappable ? 'Tap to assign someone' : undefined} data-testid={`rota-role-${role}-${d}`}>{isGap ? (tappable ? '+ fill' : 'gap') : perDay[d]}</td>
      );
    });
    return (
      <tr key={role} className="border-t" data-testid={`rota-role-${role}`}>
        <td className="py-0.5 pr-2 text-[10px] uppercase text-slate-500">{role.replace('_', ' ')}</td>
        {roleCells}
        <td />
      </tr>
    );
  });
  const gapNames = gaps.slice(0, 6).map((g) => `${g.role.replace('_', ' ')} on ${dayLabel(g.day)}`);
  const nothingPlanned = emptyDays.length === days.length;
  const gapText = nothingPlanned ? 'No planned starts this week yet — set them under "Planned starts" to see coverage and gaps.' : (gaps.length ? `${gaps.length} gap(s): ${gapNames.join(', ')}${gaps.length > 6 ? '…' : ''}` : 'Every role that works this week is covered every day.');
  return (
    <div className="border rounded-xl p-3 bg-slate-50 mb-3" data-testid="att-rota">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <p className="text-xs text-slate-700 flex-1 min-w-[240px] inline-flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> Week rota from planned starts, approved swaps (⇄) and manager assignments (*). Past days: ✓ scanned · +min late · ✕ no scan.</p>
        <button type="button" onClick={() => setStart(shift(start, -7))} className="p-1 border rounded hover:bg-white" data-testid="rota-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="border rounded px-1.5 py-1 text-xs" data-testid="rota-start" />
        <button type="button" onClick={() => setStart(shift(start, 7))} className="p-1 border rounded hover:bg-white" data-testid="rota-next"><ChevronRight className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => printRota(data)} className="inline-flex items-center gap-1 text-xs bg-slate-900 text-white rounded px-2.5 py-1.5" data-testid="rota-print"><Printer className="w-3.5 h-3.5" /> Print</button>
        <button type="button" onClick={onClose} className="text-xs underline text-slate-600" data-testid="rota-hide">Hide</button>
      </div>
      <p className={`text-xs mb-2 ${nothingPlanned ? 'text-slate-500' : (gaps.length ? 'text-rose-700 font-semibold' : 'text-emerald-700')}`} data-testid="rota-gaps">{gapText}{canManage && gaps.length > 0 ? ' — tap a red cell below to assign someone.' : ''}</p>
      {msg && <p className="text-xs text-slate-700 mb-2" data-testid="rota-msg">{msg}</p>}
      {gap && <GapFill gap={gap} rows={dataRows} onClose={() => setGap(null)} onSaved={(m) => { setMsg(m); load(); }} />}
      <div className="overflow-x-auto">
        <table className="text-xs min-w-[640px]" data-testid="rota-table">
          <thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="pb-1 pr-2">Employee</th>{heads}<th /></tr></thead>
          <tbody>{rows}</tbody>
          <tfoot>
            <tr className="border-t-2"><td className="py-1 pr-2 text-[10px] uppercase text-slate-500">Planned</td>{totals}<td /></tr>
            {roleRows}
          </tfoot>
        </table>
      </div>
    </div>
  );
};
